<template>
  <div class="p-6 md:p-10 pb-0">
    <NumberingSettings ref="numbering" settings-key="invoices" default-prefix="INV-" label="invoice" />
  </div>
  <AdminCrudPage
    collection-name="invoices"
    title="Invoices"
    singular-title="Invoice"
    :fields="fields"
    :columns="columns"
    print-route="/admin/invoices/print"
    :default-values="getDefaultValues"
    :on-added="handleAdded"
  />
</template>

<script>
import AdminCrudPage from './AdminCrudPage.vue'
import NumberingSettings from '../../components/NumberingSettings.vue'
import { getSettings, setSettings } from '../../services/settingsStore'
import { formatAmount } from '../../services/currencyStore'

function invoiceTotal(item) {
  const itemsTotal = (item.lineItems || []).reduce(
    (sum, row) => sum + (Number(row.quantity) || 0) * (Number(row.unitPrice) || 0),
    0
  )
  const total = Math.max(itemsTotal - (Number(item.discount) || 0), 0)
  return formatAmount(total, item.currency || 'PKR')
}

function todayFormatted() {
  return new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default {
  name: 'AdminInvoices',
  components: { AdminCrudPage, NumberingSettings },
  data() {
    return {
      columns: [
        { key: 'invoiceNo', label: 'Invoice No' },
        { key: 'customerName', label: 'Customer' },
        { key: 'hotelName', label: 'Hotel' },
        { key: 'total', label: 'Total', compute: invoiceTotal },
        { key: 'paymentStatus', label: 'Status' }
      ],
      fields: [
        { key: 'invoiceNo', label: 'Invoice No (auto-generated, editable)', type: 'text', required: true },
        { key: 'invoiceDate', label: 'Invoice Date (e.g. 23 Aug 2026)', type: 'text', required: true },
        { key: 'customerName', label: 'Customer Name', type: 'text', required: true },
        { key: 'customerPhone', label: 'Customer Phone', type: 'text' },
        { key: 'customerEmail', label: 'Customer Email', type: 'text' },
        { key: 'customerAddress', label: 'Customer Address', type: 'textarea' },
        {
          key: 'currency',
          label: 'Invoice Currency (set this first -- amounts below are entered in this currency)',
          type: 'select',
          options: ['PKR', 'SAR', 'USD', 'GBP']
        },
        { key: 'hotelName', label: 'Hotel Name (shown first, bold, on the invoice)', type: 'text' },
        { key: 'view', label: 'View (e.g. Kaaba View, Garden View, City View)', type: 'text' },
        { key: 'hcn', label: 'HCN (Hotel Confirmation Number)', type: 'text' },
        { key: 'checkInDate', label: 'Check-In Date', type: 'date' },
        { key: 'checkOutDate', label: 'Check-Out Date', type: 'date', minField: 'checkInDate' },
        { key: 'paxCount', label: 'Number of Passengers', type: 'number', default: 1 },
        { key: 'lineItems', label: 'Line Items (unit price in the Invoice Currency selected above)', type: 'lineItems' },
        { key: 'discount', label: 'Discount (in the Invoice Currency selected above)', type: 'number', default: 0 },
        { key: 'paymentStatus', label: 'Payment Status', type: 'select', options: ['Unpaid', 'Paid', 'Partial'] },
        { key: 'paymentMethod', label: 'Payment Method (e.g. Bank Transfer, JazzCash)', type: 'text' },
        { key: 'hasExtra', label: 'Something Extra?', type: 'boolean', checkboxLabel: 'Add an extra item to this invoice' },
        { key: 'extraTitle', label: 'Extra Title', type: 'text', showIf: 'hasExtra' },
        { key: 'extraDescription', label: 'Extra Description', type: 'textarea', showIf: 'hasExtra' },
        { key: 'notes', label: 'Notes / Terms', type: 'textarea' }
      ]
    }
  },
  methods: {
    async getDefaultValues() {
      const settings = await getSettings('invoices', { prefix: 'INV-', nextNumber: 1001 })
      return {
        invoiceNo: `${settings.prefix}${settings.nextNumber}`,
        invoiceDate: todayFormatted()
      }
    },
    async handleAdded() {
      const settings = await getSettings('invoices', { prefix: 'INV-', nextNumber: 1001 })
      await setSettings('invoices', { ...settings, nextNumber: settings.nextNumber + 1 })
      if (this.$refs.numbering) await this.$refs.numbering.refresh()
    }
  }
}
</script>
